import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Calendar,
  DollarSign,
  Target,
  Building2,
  User,
  Package,
  Plus,
  Trash2,
  Lock,
  Globe,
  Info,
  ChevronDown,
  Check,
} from "lucide-react";
import { useNotify, useGetList } from "ra-core";

import {
  type EntityType,
  type FieldDefinition,
  type FilterCondition,
  type FilterOperator,
  type AdvancedFilter,
  BASE_ENTITY_FIELDS,
  OPERATORS_BY_FIELD_TYPE,
  ENTITIES,
} from "./filterTypes";
import { useDealCustomFields } from "../useDealCustomFields";
import { usePipelines } from "../usePipelines";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import type { Sale, Tag } from "../../types";

interface AdvancedFilterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filterToEdit?: AdvancedFilter | null;
  defaultEntity?: EntityType;
  onSaveFilter: (
    filter: Omit<AdvancedFilter, "id" | "createdAt" | "updatedAt"> & {
      id?: string;
    },
  ) => void;
  onPreviewFilter?: (filter: AdvancedFilter) => void;
}

export function AdvancedFilterModal({
  open,
  onOpenChange,
  filterToEdit,
  defaultEntity = "deal",
  onSaveFilter,
  onPreviewFilter,
}: AdvancedFilterModalProps) {
  const notify = useNotify();
  const { fields: customFields } = useDealCustomFields();
  const { pipelines } = usePipelines();
  const {
    dealCategories,
    leadTemperatures,
    leadSources,
    companySectors,
    taskTypes,
  } = useConfigurationContext();

  const { data: salesList } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });

  const { data: tagsList } = useGetList<Tag>("tags", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  // Estado do formulário
  const [filterName, setFilterName] = useState("");
  const [isNameManuallyEdited, setIsNameManuallyEdited] = useState(false);
  const [visibility, setVisibility] = useState<"private" | "shared">("private");
  const [saveColumns, setSaveColumns] = useState(false);
  const [andConditions, setAndConditions] = useState<FilterCondition[]>([]);
  const [orConditions, setOrConditions] = useState<FilterCondition[]>([]);

  // Carrega filtro ao abrir ou ao mudar filterToEdit
  useEffect(() => {
    if (filterToEdit) {
      setFilterName(filterToEdit.name);
      setIsNameManuallyEdited(true); // filtro existente: nome já foi salvo pelo usuário
      setVisibility(filterToEdit.visibility);
      setSaveColumns(!!filterToEdit.saveSelectedColumns);
      setAndConditions(
        filterToEdit.andConditions && filterToEdit.andConditions.length > 0
          ? filterToEdit.andConditions
          : [createNewCondition(defaultEntity)],
      );
      setOrConditions(filterToEdit.orConditions || []);
    } else {
      setFilterName("");
      setIsNameManuallyEdited(false);
      setVisibility("private");
      setSaveColumns(false);
      setAndConditions([createNewCondition(defaultEntity)]);
      setOrConditions([]);
    }
  }, [filterToEdit, open, defaultEntity]);

  // Cria uma nova condição com defaults válidos
  function createNewCondition(entity: EntityType = "deal"): FilterCondition {
    const fields = getFieldsForEntity(entity);
    const firstField = fields[0];
    const operators = firstField
      ? OPERATORS_BY_FIELD_TYPE[firstField.type] || ["é"]
      : ["é"];
    return {
      id:
        "cond_" +
        Date.now().toString(36) +
        "_" +
        Math.random().toString(36).substring(2, 6),
      entity,
      fieldId: firstField?.id || "",
      operator: (operators[0] || "é") as FilterOperator,
      value: "",
    };
  }

  // Mescla os campos padrão com os campos customizados dinâmicos e opções configuradas
  const allFieldsByEntity = useMemo(() => {
    const base = { ...BASE_ENTITY_FIELDS };

    // Vendedores formatados
    const salesChoices =
      salesList?.map((s) => ({
        value: String(s.id),
        label: `${s.first_name} ${s.last_name}`.trim(),
      })) || [];

    // Tags formatadas
    const tagChoices =
      tagsList?.map((t) => ({
        value: String(t.id),
        label: t.name,
      })) || [];

    // Enriquecer campos de Negócio com etapas (com indicação de funil), categorias e campos customizados
    const enrichedDealFields: FieldDefinition[] = base.deal.map((f) => {
      if (f.id === "funil_id") {
        const pipelineChoices = pipelines.map((p) => ({
          value: String(p.id),
          label: p.nome,
        }));
        return {
          ...f,
          choices:
            pipelineChoices.length > 0
              ? pipelineChoices
              : [{ value: "principal", label: "Funil Padrão" }],
        };
      }
      if (f.id === "stage") {
        const hasMultiple = pipelines.length > 1;
        const stageChoices = pipelines.flatMap((p) =>
          (p.etapas || []).map((s) => ({
            value: s.value,
            label: hasMultiple ? `${s.label} (${p.nome})` : s.label,
          })),
        );
        const uniqueStages = Array.from(
          new Map(stageChoices.map((s) => [s.value, s])).values(),
        );
        return {
          ...f,
          choices:
            uniqueStages.length > 0
              ? uniqueStages
              : [
                  { value: "lead", label: "Lead" },
                  { value: "opportunity", label: "Oportunidade" },
                  { value: "proposal", label: "Proposta" },
                  { value: "negotiation", label: "Negociação" },
                  { value: "won", label: "Ganho" },
                  { value: "lost", label: "Perdido" },
                ],
        };
      }
      if (f.id === "category") {
        return {
          ...f,
          choices:
            dealCategories && dealCategories.length > 0
              ? dealCategories.map((c) => ({
                  value: c.value,
                  label: c.label,
                }))
              : f.choices,
        };
      }
      if (f.id === "origem" || f.id === "canal_origem") {
        return {
          ...f,
          choices:
            leadSources && leadSources.length > 0
              ? leadSources.map((s) => ({
                  value: s.value,
                  label: s.label,
                }))
              : f.choices,
        };
      }
      if (f.id === "temperatura") {
        return {
          ...f,
          choices:
            leadTemperatures && leadTemperatures.length > 0
              ? leadTemperatures.map((t) => ({
                  value: t.value,
                  label: t.label,
                }))
              : f.choices,
        };
      }
      if (f.id === "sales_id" || f.id === "created_by") {
        return {
          ...f,
          choices: salesChoices,
        };
      }
      if (f.id === "tags") {
        return {
          ...f,
          choices: tagChoices,
        };
      }
      return f;
    });

    // Adicionar campos customizados criados no CRM
    const dynamicDealFields: FieldDefinition[] = (customFields || []).map(
      (cf) => {
        let fType = "text" as any;
        if (cf.type === "number") fType = "number";
        else if (cf.type === "currency") fType = "currency";
        else if (cf.type === "date") fType = "date";
        else if (cf.type === "select") fType = "single_choice";

        return {
          id: cf.id,
          label: `${cf.name} (Personalizado)`,
          type: fType,
          entity: "deal",
          choices: cf.options?.map((opt) => ({ value: opt, label: opt })),
          isCustom: true,
        };
      },
    );

    // Enriquecer campos de Organização
    const enrichedCompanyFields: FieldDefinition[] = base.company.map((f) => {
      if (f.id === "sector") {
        return {
          ...f,
          choices:
            companySectors && companySectors.length > 0
              ? companySectors.map((cs) => ({
                  value: cs.value,
                  label: cs.label,
                }))
              : [
                  { value: "tecnologia", label: "Tecnologia" },
                  { value: "servicos", label: "Serviços" },
                  { value: "comercio", label: "Comércio" },
                  { value: "industria", label: "Indústria" },
                  { value: "outro", label: "Outro" },
                ],
        };
      }
      if (f.id === "sales_id") {
        return {
          ...f,
          choices: salesChoices,
        };
      }
      if (f.id === "tags") {
        return {
          ...f,
          choices: tagChoices,
        };
      }
      return f;
    });

    // Enriquecer campos de Contato (Pessoa)
    const enrichedContactFields: FieldDefinition[] = base.contact.map((f) => {
      if (f.id === "sales_id") {
        return {
          ...f,
          choices: salesChoices,
        };
      }
      if (f.id === "tags") {
        return {
          ...f,
          choices: tagChoices,
        };
      }
      return f;
    });

    // Enriquecer campos de Atividade
    const enrichedActivityFields: FieldDefinition[] = base.activity.map((f) => {
      if (f.id === "type") {
        return {
          ...f,
          choices:
            taskTypes && taskTypes.length > 0
              ? taskTypes.map((tt) => ({
                  value: tt.value,
                  label: tt.label,
                }))
              : f.choices,
        };
      }
      if (f.id === "assigned_to" || f.id === "created_by") {
        return {
          ...f,
          choices: salesChoices,
        };
      }
      return f;
    });

    return {
      ...base,
      deal: [...enrichedDealFields, ...dynamicDealFields],
      company: enrichedCompanyFields,
      contact: enrichedContactFields,
      activity: enrichedActivityFields,
    };
  }, [
    customFields,
    pipelines,
    dealCategories,
    leadSources,
    leadTemperatures,
    companySectors,
    taskTypes,
    salesList,
    tagsList,
  ]);

  function getFieldsForEntity(entity: EntityType): FieldDefinition[] {
    return allFieldsByEntity[entity] || [];
  }

  // Gera nome automático baseado nas condições
  const generateFilterName = useMemo(() => {
    const allConds = [...andConditions, ...orConditions].filter(
      (c) => c.fieldId && c.operator,
    );
    if (allConds.length === 0) return "";

    const parts = allConds.slice(0, 2).map((cond) => {
      const fields = getFieldsForEntity(cond.entity);
      const field = fields.find((f) => f.id === cond.fieldId);
      const fieldLabel = field?.label || cond.fieldId;

      const isNoValue =
        cond.operator === "esta vazio" || cond.operator === "não esta vazio";
      if (isNoValue) {
        return `${fieldLabel} ${cond.operator}`;
      }

      // Resolve o label do valor se o campo tem choices
      let valueLabel = cond.value ?? "";
      if (
        field?.choices &&
        field.choices.length > 0 &&
        cond.value !== undefined &&
        cond.value !== null
      ) {
        const found = field.choices.find(
          (ch) => String(ch.value) === String(cond.value),
        );
        if (found) valueLabel = found.label;
      }

      if (!valueLabel) return `${fieldLabel} ${cond.operator}`;
      return `${fieldLabel} ${cond.operator} ${valueLabel}`;
    });

    const name = parts.join(" • ");
    return allConds.length > 2 ? `${name} • +${allConds.length - 2}` : name;
  }, [andConditions, orConditions, allFieldsByEntity]);

  // Auto-preenche o nome quando as condições mudam (só se não foi editado manualmente)
  useEffect(() => {
    if (!isNameManuallyEdited && generateFilterName) {
      setFilterName(generateFilterName);
    }
  }, [generateFilterName, isNameManuallyEdited]);

  // Manipuladores para Bloco TODAS (AND)
  const handleAddAndCondition = () => {
    setAndConditions((prev) => [...prev, createNewCondition("deal")]);
  };

  const handleUpdateAndCondition = (
    index: number,
    updates: Partial<FilterCondition>,
  ) => {
    setAndConditions((prev) =>
      prev.map((c, i) => (i === index ? { ...c, ...updates } : c)),
    );
  };

  const handleDeleteAndCondition = (index: number) => {
    setAndConditions((prev) => prev.filter((_, i) => i !== index));
  };

  // Manipuladores para Bloco QUALQUER (OR)
  const handleAddOrCondition = () => {
    setOrConditions((prev) => [...prev, createNewCondition("deal")]);
  };

  const handleUpdateOrCondition = (
    index: number,
    updates: Partial<FilterCondition>,
  ) => {
    setOrConditions((prev) =>
      prev.map((c, i) => (i === index ? { ...c, ...updates } : c)),
    );
  };

  const handleDeleteOrCondition = (index: number) => {
    setOrConditions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!filterName.trim()) {
      notify("Por favor, digite um nome para o filtro.", { type: "warning" });
      return;
    }

    const cleanedAnd = andConditions.filter((c) => c.fieldId && c.operator);
    const cleanedOr = orConditions.filter((c) => c.fieldId && c.operator);

    if (cleanedAnd.length === 0 && cleanedOr.length === 0) {
      notify("Adicione pelo menos uma condição ao filtro.", {
        type: "warning",
      });
      return;
    }

    onSaveFilter({
      id: filterToEdit?.id,
      name: filterName.trim(),
      visibility,
      saveSelectedColumns: saveColumns,
      andConditions: cleanedAnd,
      orConditions: cleanedOr,
    });

    notify("Filtro salvo com sucesso!", { type: "success" });
    onOpenChange(false);
  };

  const handlePreview = () => {
    const cleanedAnd = andConditions.filter((c) => c.fieldId && c.operator);
    const cleanedOr = orConditions.filter((c) => c.fieldId && c.operator);
    const previewFilter: AdvancedFilter = {
      id: filterToEdit?.id || "preview_temp",
      name: filterName || "Pré-visualização",
      visibility,
      saveSelectedColumns: saveColumns,
      andConditions: cleanedAnd,
      orConditions: cleanedOr,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    onPreviewFilter?.(previewFilter);
    notify("Filtro aplicado em modo de pré-visualização.", { type: "info" });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto p-6 gap-5 bg-background">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-foreground">
            {filterToEdit ? "Editar filtro" : "Criar novo filtro"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* SEÇÃO 1: Atender a TODAS estas condições */}
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <span>Atender a TODAS estas condições</span>
              <Info className="w-4 h-4 text-muted-foreground/70" />
            </div>

            <div className="p-3 bg-muted/40 rounded-xl border border-border/70 space-y-2.5">
              {andConditions.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2 italic text-center">
                  Nenhuma condição obrigatória definida.
                </p>
              ) : (
                andConditions.map((condition, idx) => (
                  <ConditionRow
                    key={condition.id || idx}
                    condition={condition}
                    fields={getFieldsForEntity(condition.entity)}
                    onUpdate={(updates) =>
                      handleUpdateAndCondition(idx, updates)
                    }
                    onDelete={() => handleDeleteAndCondition(idx)}
                  />
                ))
              )}

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAddAndCondition}
                className="text-primary hover:text-primary hover:bg-primary/10 font-semibold text-xs h-8 px-2 gap-1.5 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                Adicionar condição
              </Button>
            </div>
          </div>

          {/* SEÇÃO 2: E atender a QUALQUER destas condições */}
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <span>E atender a QUALQUER destas condições</span>
              <Info className="w-4 h-4 text-muted-foreground/70" />
            </div>

            <div className="p-3 bg-muted/40 rounded-xl border border-border/70 space-y-2.5">
              {orConditions.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2 italic text-center">
                  Nenhuma condição alternativa configurada.
                </p>
              ) : (
                orConditions.map((condition, idx) => (
                  <ConditionRow
                    key={condition.id || idx}
                    condition={condition}
                    fields={getFieldsForEntity(condition.entity)}
                    onUpdate={(updates) =>
                      handleUpdateOrCondition(idx, updates)
                    }
                    onDelete={() => handleDeleteOrCondition(idx)}
                  />
                ))
              )}

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAddOrCondition}
                className="text-primary hover:text-primary hover:bg-primary/10 font-semibold text-xs h-8 px-2 gap-1.5 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                Adicionar condição
              </Button>
            </div>
          </div>

          {/* RODAPÉ: Nome, Visibilidade e Checkbox de colunas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="filter-name" className="text-xs font-semibold">
                  Nome do filtro
                </Label>
                {!isNameManuallyEdited && generateFilterName && (
                  <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-md font-medium">
                    gerado automaticamente
                  </span>
                )}
                {isNameManuallyEdited && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsNameManuallyEdited(false);
                      setFilterName(generateFilterName);
                    }}
                    className="text-[10px] text-primary hover:underline cursor-pointer"
                  >
                    ↺ usar automático
                  </button>
                )}
              </div>
              <Input
                id="filter-name"
                placeholder="Ex: Negócios Quentes, Deals Q3..."
                value={filterName}
                onChange={(e) => {
                  setFilterName(e.target.value);
                  setIsNameManuallyEdited(true);
                }}
                className="h-9 bg-background"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Visibilidade:</Label>
              <Select
                value={visibility}
                onValueChange={(val: any) => setVisibility(val)}
              >
                <SelectTrigger className="h-9 bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">
                    <div className="flex items-center gap-2">
                      <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                      <span>Privado</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="shared">
                    <div className="flex items-center gap-2">
                      <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                      <span>Compartilhado</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2 flex items-center space-x-2 pt-1">
              <Checkbox
                id="save-cols"
                checked={saveColumns}
                onCheckedChange={(checked) => setSaveColumns(!!checked)}
              />
              <label
                htmlFor="save-cols"
                className="text-xs text-muted-foreground font-medium cursor-pointer select-none flex items-center gap-1.5"
              >
                <span>Salvar as colunas selecionadas com o filtro</span>
                <Info className="w-3.5 h-3.5 text-muted-foreground/60" />
              </label>
            </div>
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between w-full pt-4 border-t border-border/60">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer"
          >
            Cancelar
          </Button>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handlePreview}
              className="cursor-pointer"
            >
              Pré-visualização
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold cursor-pointer px-6"
            >
              Salvar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Subcomponente: Linha de Condição com EM QUE + Entidade + Campo + Operador + Valor + Lixeira
interface ConditionRowProps {
  condition: FilterCondition;
  fields: FieldDefinition[];
  onUpdate: (updates: Partial<FilterCondition>) => void;
  onDelete: () => void;
}

function ConditionRow({
  condition,
  fields,
  onUpdate,
  onDelete,
}: ConditionRowProps) {
  const [fieldPopoverOpen, setFieldPopoverOpen] = useState(false);

  const selectedField =
    fields.find((f) => f.id === condition.fieldId) || fields[0];
  const fieldType = selectedField?.type || "text";
  const operators = OPERATORS_BY_FIELD_TYPE[fieldType] || ["é"];

  // Se trocar a entidade
  const handleEntityChange = (newEntity: EntityType) => {
    const newFields = BASE_ENTITY_FIELDS[newEntity] || [];
    const firstF = newFields[0];
    const ops = firstF ? OPERATORS_BY_FIELD_TYPE[firstF.type] || ["é"] : ["é"];
    onUpdate({
      entity: newEntity,
      fieldId: firstF?.id || "",
      operator: (ops[0] || "é") as FilterOperator,
      value: "",
    });
  };

  // Se trocar o campo
  const handleFieldChange = (newFieldId: string) => {
    const f = fields.find((item) => item.id === newFieldId);
    const ops = f ? OPERATORS_BY_FIELD_TYPE[f.type] || ["é"] : ["é"];
    onUpdate({
      fieldId: newFieldId,
      operator: (ops[0] || "é") as FilterOperator,
      value: "",
    });
    setFieldPopoverOpen(false);
  };

  // Renderiza o ícone da entidade selecionada
  const renderEntityIcon = (ent: EntityType) => {
    switch (ent) {
      case "activity":
        return <Calendar className="w-4 h-4 text-blue-500" />;
      case "deal":
        return <DollarSign className="w-4 h-4 text-emerald-500" />;
      case "lead":
        return <Target className="w-4 h-4 text-purple-500" />;
      case "company":
        return <Building2 className="w-4 h-4 text-amber-500" />;
      case "contact":
        return <User className="w-4 h-4 text-indigo-500" />;
      case "product":
        return <Package className="w-4 h-4 text-orange-500" />;
      default:
        return <DollarSign className="w-4 h-4" />;
    }
  };

  const isNoValueOperator =
    condition.operator === "esta vazio" ||
    condition.operator === "não esta vazio";

  return (
    <div className="flex items-center gap-2 flex-wrap bg-background p-2 rounded-lg border border-border shadow-2xs">
      {/* Badge "EM QUE" */}
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted px-2 py-1 rounded-md">
        EM QUE
      </span>

      {/* Dropdown de Entidade com Ícone */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-2 px-2.5 font-medium border-border/80 cursor-pointer bg-background"
          >
            {renderEntityIcon(condition.entity)}
            <span className="text-xs">
              {ENTITIES.find((e) => e.id === condition.entity)?.label ||
                condition.entity}
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground ml-0.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          {ENTITIES.map((ent) => (
            <DropdownMenuItem
              key={ent.id}
              onClick={() => handleEntityChange(ent.id)}
              className="flex items-center justify-between text-xs cursor-pointer py-1.5"
            >
              <div className="flex items-center gap-2">
                {renderEntityIcon(ent.id)}
                <span>{ent.label}</span>
              </div>
              {condition.entity === ent.id && (
                <Check className="w-4 h-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Combobox pesquisável de Campo */}
      <Popover open={fieldPopoverOpen} onOpenChange={setFieldPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            role="combobox"
            aria-expanded={fieldPopoverOpen}
            className="h-8 min-w-[170px] flex-1 sm:flex-initial justify-between px-2.5 font-normal text-xs border-border/80 cursor-pointer bg-background"
          >
            <span className="truncate">
              {selectedField?.label || "Selecione o campo"}
            </span>
            <ChevronDown className="w-3.5 h-3.5 ml-1 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-72 p-0"
          align="start"
          side="bottom"
          // Evita que o clique no Command feche o Dialog pai
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command>
            <CommandInput
              placeholder="Pesquisar campo..."
              className="h-9 text-xs"
            />
            <CommandList className="max-h-56">
              <CommandEmpty className="py-4 text-xs text-center text-muted-foreground">
                Nenhum campo encontrado.
              </CommandEmpty>
              <CommandGroup>
                {fields.map((f) => (
                  <CommandItem
                    key={f.id}
                    value={f.label}
                    onSelect={() => handleFieldChange(f.id)}
                    className="text-xs cursor-pointer"
                  >
                    <Check
                      className={`mr-2 h-3.5 w-3.5 shrink-0 ${
                        condition.fieldId === f.id
                          ? "opacity-100 text-primary"
                          : "opacity-0"
                      }`}
                    />
                    <span>{f.label}</span>
                    {f.isCustom && (
                      <span className="ml-auto text-[10px] text-muted-foreground bg-muted px-1 rounded">
                        custom
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Dropdown de Operador */}
      <div className="min-w-[130px] flex-1 sm:flex-initial">
        <Select
          value={condition.operator}
          onValueChange={(op: any) => onUpdate({ operator: op })}
        >
          <SelectTrigger className="h-8 text-xs bg-background">
            <SelectValue placeholder="Operador" />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {operators.map((op) => (
              <SelectItem key={op} value={op} className="text-xs">
                {op}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Campo de Valor (se não for "esta vazio" / "não esta vazio") */}
      {!isNoValueOperator && (
        <div className="min-w-[150px] flex-1">
          {selectedField?.choices && selectedField.choices.length > 0 ? (
            <Select
              value={
                condition.value !== undefined && condition.value !== null
                  ? String(condition.value)
                  : ""
              }
              onValueChange={(val) => onUpdate({ value: val })}
            >
              <SelectTrigger className="h-8 text-xs bg-background">
                <SelectValue placeholder="Selecione a opção" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {selectedField.choices.map((c) => (
                  <SelectItem
                    key={c.value}
                    value={String(c.value)}
                    className="text-xs"
                  >
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : fieldType === "date" ? (
            <Input
              type="date"
              className="h-8 text-xs bg-background"
              value={condition.value || ""}
              onChange={(e) => onUpdate({ value: e.target.value })}
            />
          ) : fieldType === "time" ? (
            <Input
              type="time"
              className="h-8 text-xs bg-background"
              value={condition.value || ""}
              onChange={(e) => onUpdate({ value: e.target.value })}
            />
          ) : fieldType === "number" || fieldType === "currency" ? (
            <Input
              type="number"
              step="any"
              placeholder="Digite o valor..."
              className="h-8 text-xs bg-background"
              value={condition.value ?? ""}
              onChange={(e) => onUpdate({ value: e.target.value })}
            />
          ) : (
            <Input
              type="text"
              placeholder="Digite o valor..."
              className="h-8 text-xs bg-background"
              value={condition.value ?? ""}
              onChange={(e) => onUpdate({ value: e.target.value })}
            />
          )}
        </div>
      )}

      {/* Botão de Excluir Condição */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onDelete}
        className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0 ml-auto cursor-pointer"
        title="Remover condição"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}
